import React, { useState, useEffect } from "react";
import appointmentService from "../services/appointmentService";
import "../styles/appointments.css";

const Toasts = ({ success, error }) => {
  if (!success && !error) return null;
  return (
    <div className="toast-container">
      {success && <div className="toast toast-success">{success}</div>}
      {error && <div className="toast toast-error">{error}</div>}
    </div>
  );
};

const AksarayBuildingAppointment = () => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    event_type: "",
    unit_responsible_notification: "Yok",
    participant_count: 1,
    requested_room: "",
    requesting_unit: "",
    requesting_person: "",
    request_reason: "",
    appointment_date: "",
    appointment_time: "",
    appointment_end_time: "",
    kitchen_use: false,
    other_preferences: ""
  });

  const [rooms, setRooms] = useState([]);
  const [units, setUnits] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("form");
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(""), 4000);
    return () => clearTimeout(timer);
  }, [success]);

  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(""), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    fetchConstants();
    if (activeTab === "list") {
      fetchAppointments();
    }
  }, [activeTab]);

  const fetchConstants = async () => {
    try {
      const [roomsRes, unitsRes, typesRes] = await Promise.all([
        appointmentService.getRooms(token),
        appointmentService.getUnits(token),
        appointmentService.getEventTypes(token)
      ]);
      setRooms(roomsRes.rooms || []);
      setUnits(unitsRes.units || []);
      setEventTypes(typesRes.event_types || []);
    } catch (err) {
      console.error("Veriler getirilemedi:", err);
    }
  };

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const response = await appointmentService.getAksarayAppointments(null, null, token);
      setAppointments(response.appointments || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : (name === "participant_count" ? parseInt(value) : value)
    }));
  };

  const isValidDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(new Date(`${value}T00:00:00`).getTime());

  const isValidTime = (value) => /^([01]\d|2[0-3]):[0-5]\d$/.test(value);

  const isFutureAppointment = (dateValue, timeValue) => {
    if (!isValidDate(dateValue) || !isValidTime(timeValue)) return false;

    const appointmentDateTime = new Date(`${dateValue}T${timeValue}:00`);
    if (Number.isNaN(appointmentDateTime.getTime())) return false;

    return appointmentDateTime > new Date();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!isValidDate(formData.appointment_date)) {
      setError("Geçerli bir tarih girin. Format: YYYY-MM-DD");
      return;
    }

    if (!isValidTime(formData.appointment_time)) {
      setError("Geçerli bir giriş saati girin. Format: HH:MM");
      return;
    }

    if (!isValidTime(formData.appointment_end_time)) {
      setError("Geçerli bir çıkış saati girin. Format: HH:MM");
      return;
    }

    if (formData.appointment_end_time <= formData.appointment_time) {
      setError("Çıkış saati giriş saatinden sonra olmalı.");
      return;
    }

    if (!isFutureAppointment(formData.appointment_date, formData.appointment_time)) {
      setError("Geçmiş bir tarih veya saat için randevu oluşturulamaz.");
      return;
    }

    try {
      setLoading(true);
      await appointmentService.createAksarayAppointment(formData, token);
      
      setSuccess("Randevu başarıyla oluşturuldu!");
      setFormData({
        first_name: "",
        last_name: "",
        phone: "",
        email: "",
        event_type: "",
        unit_responsible_notification: "Yok",
        participant_count: 1,
        requested_room: "",
        requesting_unit: "",
        requesting_person: "",
        request_reason: "",
        appointment_date: "",
        appointment_time: "",
        appointment_end_time: "",
        kitchen_use: false,
        other_preferences: ""
      });

      setTimeout(() => {
        setActiveTab("list");
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (appointmentId) => {
    if (window.confirm("Randevuyu iptal etmek istediğinize emin misiniz?")) {
      try {
        setLoading(true);
        await appointmentService.cancelAksarayAppointment(appointmentId, token);
        setSuccess("Randevu iptal edildi!");
        fetchAppointments();
        setTimeout(() => setSuccess(""), 2000);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const STATUS_LABELS = {
    pending: "Onay Bekliyor",
    approved: "Onaylandı",
    rejected: "Reddedildi",
    cancelled: "İptal Edildi"
  };

  const formatTimeRange = (apt) =>
    apt.appointment_end_time ? `${apt.appointment_time} - ${apt.appointment_end_time}` : apt.appointment_time;

  const getRoomName = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    return room ? room.name : roomId;
  };

  return (
    <div className="appointment-container">
      <div className="appointment-header">
        <h2>Aksaray Hanımlar Binası - Randevu Sistemi</h2>
        <p>Salon, mescid ve atölye kullanımı için randevu alabilirsiniz</p>
      </div>

      <div className="appointment-tabs">
        <button
          className={`tab-btn ${activeTab === "form" ? "active" : ""}`}
          onClick={() => setActiveTab("form")}
        >
          Yeni Randevu
        </button>
        <button
          className={`tab-btn ${activeTab === "list" ? "active" : ""}`}
          onClick={() => setActiveTab("list")}
        >
          Randevularım
        </button>
      </div>

      <Toasts success={success} error={error} />

      {activeTab === "form" && (
        <form className="appointment-form" onSubmit={handleSubmit}>
          <fieldset>
            <legend>KİŞİSEL BİLGİLER</legend>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="first_name">Ad *</label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  placeholder="Adınız"
                />
              </div>

              <div className="form-group">
                <label htmlFor="last_name">Soyad *</label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  placeholder="Soyadınız"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="phone">Telefon Numarası *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="05551234567"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Mail Adresi *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="ornek@example.com"
                />
              </div>
            </div>
          </fieldset>

          <fieldset>
            <legend>ETKİNLİK BİLGİLERİ</legend>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="event_type">Etkinlik Türü *</label>
                <select
                  id="event_type"
                  name="event_type"
                  value={formData.event_type}
                  onChange={handleChange}
                  required
                >
                  <option value="">Seçiniz</option>
                  {eventTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="participant_count">Katılımcı Sayısı *</label>
                <input
                  type="number"
                  id="participant_count"
                  name="participant_count"
                  value={formData.participant_count}
                  onChange={handleChange}
                  min="1"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="requested_room">Talep Edilen Salon *</label>
                <select
                  id="requested_room"
                  name="requested_room"
                  value={formData.requested_room}
                  onChange={handleChange}
                  required
                >
                  <option value="">Seçiniz</option>
                  {rooms.map(room => (
                    <option key={room.id} value={room.id}>
                      {room.name} {room.capacity ? `(${room.capacity} kişi)` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="unit_responsible_notification">Birim Sorumlusu Haberi *</label>
                <select
                  id="unit_responsible_notification"
                  name="unit_responsible_notification"
                  value={formData.unit_responsible_notification}
                  onChange={handleChange}
                  required
                >
                  <option value="Var">Var</option>
                  <option value="Yok">Yok</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="request_reason">Talep Sebebi (Detaylı) *</label>
              <textarea
                id="request_reason"
                name="request_reason"
                value={formData.request_reason}
                onChange={handleChange}
                required
                placeholder="Neden bu salonu/atölyeyi kullanmak istediğinizi açıklayınız"
                rows="4"
              />
            </div>
          </fieldset>

          <fieldset>
            <legend>TALEP BİLGİLERİ</legend>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="requesting_unit">Talep Eden Birim *</label>
                <select
                  id="requesting_unit"
                  name="requesting_unit"
                  value={formData.requesting_unit}
                  onChange={handleChange}
                  required
                >
                  <option value="">Seçiniz</option>
                  {units.map(unit => (
                    <option key={unit} value={unit}>{unit}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="requesting_person">Talep Eden Kişi *</label>
                <input
                  type="text"
                  id="requesting_person"
                  name="requesting_person"
                  value={formData.requesting_person}
                  onChange={handleChange}
                  required
                  placeholder="Ad Soyad"
                />
              </div>
            </div>
          </fieldset>

          <fieldset>
            <legend>TARIH & SAAT</legend>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="appointment_date">Tarih *</label>
                <input
                  type="date"
                  id="appointment_date"
                  name="appointment_date"
                  value={formData.appointment_date}
                  onChange={handleChange}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="appointment_time">Giriş Saati *</label>
                <input
                  type="time"
                  id="appointment_time"
                  name="appointment_time"
                  value={formData.appointment_time}
                  onChange={handleChange}
                  min={formData.appointment_date === new Date().toISOString().split('T')[0] ? new Date().toTimeString().slice(0, 5) : undefined}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="appointment_end_time">Çıkış Saati *</label>
                <input
                  type="time"
                  id="appointment_end_time"
                  name="appointment_end_time"
                  value={formData.appointment_end_time}
                  onChange={handleChange}
                  min={formData.appointment_time || undefined}
                  required
                />
              </div>
            </div>
            <p className="small-text">Binaya giriş ve çıkış saatlerini belirtiniz (örn. 10:00 - 17:00).</p>
          </fieldset>

          <fieldset>
            <legend>OLUŞTURMA TESİSLERİ</legend>
            
            <div className="form-group checkbox-group">
              <label htmlFor="kitchen_use">
                <input
                  type="checkbox"
                  id="kitchen_use"
                  name="kitchen_use"
                  checked={formData.kitchen_use}
                  onChange={handleChange}
                />
                Mutfağı kullanacak mısınız?
              </label>
            </div>

            <div className="form-group">
              <label htmlFor="other_preferences">Farklı bir tercihiniz var mı?</label>
              <textarea
                id="other_preferences"
                name="other_preferences"
                value={formData.other_preferences}
                onChange={handleChange}
                placeholder="Örn: Projeksiyon cihazı, sehpalar, vb."
                rows="3"
              />
            </div>
          </fieldset>

          <button type="submit" disabled={loading} className="btn btn-primary">
            {loading ? "Gönderiliyor..." : "Randevu Al"}
          </button>
        </form>
      )}

      {activeTab === "list" && (
        <div className="appointments-list">
          {loading ? (
            <p className="loading">Yükleniyor...</p>
          ) : appointments.length === 0 ? (
            <p className="empty-message">Henüz randevunuz yok</p>
          ) : (
            appointments.map(apt => (
              <div key={apt._id} className={`appointment-card status-${apt.status}`}>
                <div className="appointment-header-card">
                  <h3>{apt.first_name} {apt.last_name}</h3>
                  <span className={`status-badge ${apt.status}`}>{STATUS_LABELS[apt.status] || apt.status}</span>
                </div>
                <p><strong>Etkinlik:</strong> {apt.event_type}</p>
                <p><strong>Salon:</strong> {getRoomName(apt.requested_room)}</p>
                <p><strong>Birim:</strong> {apt.requesting_unit}</p>
                <p><strong>Katılımcılar:</strong> {apt.participant_count}</p>
                <p><strong>Tarih:</strong> {apt.appointment_date}</p>
                <p><strong>Saat Aralığı:</strong> {formatTimeRange(apt)}</p>
                <p><strong>Telefon:</strong> {apt.phone}</p>
                {apt.kitchen_use && <p className="badge-info">Mutfak kullanılacak</p>}
                {apt.status === "rejected" && apt.rejection_reason && (
                  <p><strong>Red Sebebi:</strong> {apt.rejection_reason}</p>
                )}
                <p className="small-text">Oluşturulma: {new Date(apt.created_at).toLocaleString('tr-TR')}</p>
                
                {apt.status === "pending" && (
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleCancel(apt._id)}
                    disabled={loading}
                  >
                    İptal Et
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default AksarayBuildingAppointment;
